import { memo, useEffect, useState, FC, useRef, useContext } from "react"
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import axios from "src/shared/axios"

import { localUserIdAtom, usersSelector } from "../../recoil/atoms/users"
import { TrulienceStateUpdater } from "../../recoil/atoms/trulience"
import {
  currentChatGenerationAtom,
  filterAvatarChatsByIdAtom
} from "../../recoil/atoms/chats"
import { SDKContext } from "../../context/SDKContext"
import { AvatarGesture } from "../../recoil/atoms/avatars/types"
import layoutState from "../../recoil/atoms/layout"
import { TRULIENCE_TEST_DEPLOY } from "../../constants"
import MOLocation from "../../util/MOLocation"
import { ConsoleLogger } from "src/util/logger"
import useConfig from "src/hooks/useConfig"
import { avatarCommunication, AvatarEvent } from "./AvatarCommunication"
import useCurrentAvatarInfo from "./hooks/useCurrentAvatarInfo"
import { queuedInspirationAtom } from "src/recoil/atoms/inspirations"
import useAuthedUser from "src/hooks/useAuthedUser"
import useTrulienceAvatarEventListener from "./hooks/useTrulienceAvatarEventListener"
import { useChatGenerationValues } from "src/hooks/useChatGenerationValues"

const logger = new ConsoleLogger()

type AvatarProps = {
  borderRadius?: string
}

const _TrulienceAvatar: FC<AvatarProps> = ({ borderRadius = "0.375rem" }) => {
  const config = useConfig()
  const avatarConfig = useCurrentAvatarInfo()
  const [avatarJWT, setAvatarJWT] = useState(null)
  const localUserId = useRecoilValue(localUserIdAtom)
  const localUser = useRecoilValue(usersSelector(localUserId))
  const updateTrulienceState = useSetRecoilState(TrulienceStateUpdater)
  const meetingClient = useContext(SDKContext)
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const isVisibleMobileFS = useRecoilValue(layoutState.visibleMobileFSTabs)
  const setFilterAvatarChatsById = useSetRecoilState(filterAvatarChatsByIdAtom)
  const queuedInspiration = useRecoilValue(queuedInspirationAtom)
  const { authedUser } = useAuthedUser()
  const initAvatar = !!avatarJWT

  const onIframeLoaded = () => {
    updateTrulienceState({ iframeHasLoaded: true })
  }

  // Use-effect to sync the local mic status with Trulience Avatar
  useEffect(() => {
    if (!initAvatar) return
    avatarCommunication.setMicStatus(localUser?.micEnabled)
  }, [localUser?.micEnabled, initAvatar])

  // This useEffect is use to toggle the joystick if any content is visible on 3d avatar
  useEffect(() => {
    if (!initAvatar) return
    if (avatarConfig.gesture == AvatarGesture.move && isVisibleMobileFS) {
      avatarCommunication.disableJoystick()
    } else {
      avatarCommunication.enableJoystick()
    }
  }, [isVisibleMobileFS, avatarConfig.gesture, initAvatar])

  const { getChatGenerationValues } = useChatGenerationValues()
  const chatGenerationValues = getChatGenerationValues()

  const cgv = chatGenerationValues?.find(
    c => c.customerAvatarId === avatarConfig.id
  )

  const setCurrentChatGeneration = useSetRecoilState(currentChatGenerationAtom)

  const generation = cgv?.generation ?? 0

  useEffect(() => {
    setCurrentChatGeneration(generation)
  }, [generation])

  const requestAvatarJWT = async () => {
    try {
      const res = await axios.post(
        meetingClient?.envConfig.TRULIENCE_AVATAR_JWT,
        {
          host: meetingClient?.envConfig.DOMAIN_HOSTNAME,
          generation
        },
        {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json"
          }
        }
      )
      if (res.status == 200) {
        setAvatarJWT(res.data.token)
      }
    } catch (err) {
      logger.error(err)
    }
  }

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // bg
      } else {
        // fg
        avatarCommunication.fixAudioContext()
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)

    // Cleanup the event listener on component unmount
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [])

  useEffect(() => {
    // request a trulience jwt for the currently logged in user
    requestAvatarJWT()

    const timeoutId = setTimeout(() => {
      onIframeLoaded()
    }, 4000)

    return () => clearTimeout(timeoutId)
  }, [])

  // GENERATE DIGITAL AVATAR URL
  const avatarId =
    MOLocation.getURLParamValue("avatarId") ?? avatarConfig.avatarId

  useEffect(() => {
    setFilterAvatarChatsById(avatarConfig?.id?.toString())
  }, [config])

  const params = {
    avatarId: avatarConfig.avatarId,
    connect: true,
    // hideFS: true,
    // hideChatInput: true,
    // hideChatHistory: true,
    // hideMicButton: true,
    // hideHangUpButton: true,
    // hideSpeakerButton: true,
    // hideToast: true,   // Hide the toast popup, as for some avatars, we don't pass the touch event.
    // disableDragging: true,
    // micOff: false,
    // speakerOffByDefault: false,
    // deviceOrientation: false,
    showFullscreenAvatar: true,
    avatarBackgroundColor: "transparent",
    registerTrlEvents: Object.values(AvatarEvent).join(","),
    sdkURL: avatarConfig.useDH
      ? "https://digitalhuman.uk/sdk/trulience.sdk.js"
      : "https://trulience.com/sdk/trulience.sdk.js",
    loadingBarColor: "rgb(250, 247, 3)",
    // DevConsole: true,
    TraceFunc: true
    // lsloglevel: "log"
  }

  if (avatarJWT) params["token"] = avatarJWT

  const queryString = Object.keys(params)
    .map(key => encodeURIComponent(key) + "=" + params[key])
    .join("&")

  const additionalArgs = avatarConfig.additionalArgs
    ? avatarConfig.additionalArgs.join("&")
    : ""

  const domain = "trulience.com"

  const digitalAvatarUrl = `https://${domain}/apps/public/trulience-ai?${additionalArgs}${
    additionalArgs ? "&" : ""
  }${queryString}`

  // This hook subscribe all the event send by the trulience iframe
  useTrulienceAvatarEventListener(initAvatar, { queuedInspiration, authedUser })

  return (
    <>
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          borderRadius: borderRadius,
          overflow: "clip",
          pointerEvents: TRULIENCE_TEST_DEPLOY
            ? "unset"
            : avatarConfig.gesture == AvatarGesture.move
            ? "unset" // Allow pointer events to pass through to the iframe.
            : "none" // Don't allow pointer events to pass to support dragging
        }}
      >
        {initAvatar && (
          <iframe
            // onLoad={onIframeLoaded}
            id="trl-iframe"
            ref={iframeRef}
            allow="microphone; fullscreen; accelerometer; magnetometer; xr-spatial-tracking; autoplay; encrypted-media; gyroscope; picture-in-picture"
            src={
              avatarConfig.useDebugUrl
                ? avatarConfig.debugUrl
                : digitalAvatarUrl
            }
            // src={"https://trulience.com/avatar/5406115683228403317?connect=true&hideFS=true&hideChatInput=true&hideChatHistory=true&hideMicButton=true&hideHangUpButton=true&hideSpeakerButton=true&hideToast=true&disableDragging=true&micOff=false&speakerOffByDefault=false&deviceOrientation=false&dialPageBackground=#000000&DevConsole=true&sttSource=none"}
            // src={`http://localhost:8999/avatar/${"1299410034157110631"}?connect=true&hideChatInput=true&hideFS=true&hideChatHistory=true&connect=true&hideMicButton=true&hideHangUpButton=true&speakerOffByDefault=false&hideSpeakerButton=false&micOff=${true}`}
            // src={`http://localhost:8999/avatar/${avatarConfig.avatarId}?connect=true&hideChatInput=true&hideFS=true&hideChatHistory=true&connect=true&hideMicButton=false&hideHangUpButton=true&speakerOffByDefault=false&hideSpeakerButton=false&micOff=${micOffInitial}`}
            // src={`https://digitalhuman.uk/avatar/${avatarConfig.avatarId}?hideChatInput=true&hideFS=true&hideChatHistory=true&connect=true&hideMicButton=true&hideHangUpButton=true&speakerOffByDefault=false&hideSpeakerButton=true&micOff=${micOffInitial}&disableDragging=true&deviceOrientation=${avatarConfig.gesture == AvatarGesture.move}&lsloglevel=log`}
            // src={TRULIENCE_TEST_DEPLOY ? `https://matt.trulience.com/avatar/2628251173150802168?connect=true&deviceOrientation=true&registerTrlEvents=mic-update,mic-access,speaker-update,trl-chat,trl-respond-avatar-photo-url` : `https://${domain}/avatar/${avatarId}?hideChatInput=true&hideFS=true&hideChatHistory=true&connect=true&hideMicButton=true&hideHangUpButton=true&speakerOffByDefault=false&hideSpeakerButton=true&micOff=${micOffInitial}&disableDragging=true&deviceOrientation=${supportsMoveGesture}&registerTrlEvents=mic-update,mic-access,speaker-update,trl-chat,trl-respond-avatar-photo-url&dialPageBackground=${roomStyles?.alertBackgroundColor}&hideToast=${hideToast}&DevConsole=true${avatarJWT != null ? '&token=' + avatarJWT : ''}`}
            // src={`https://digitalhuman.uk/avatar/${avatarConfig.avatarId}?connect=true&hideChatInput=true&hideFS=true&hideChatHistory=true&connect=true&hideMicButton=true&hideHangUpButton=true&speakerOffByDefault=false&hideSpeakerButton=true&micOff=${micOffInitial}&disableDragging=true&lsloglevel=log`}
            // src={`https://matt.trulience.com/avatar/2628251173150802168?connect=true`}
            // src={`http://127.0.0.1:8081/avatar/${avatarConfig.avatarId}?connect=true&hideChatInput=true&hideFS=true&hideChatHistory=true&connect=true&hideMicButton=true&hideHangUpButton=true&speakerOffByDefault=false&hideSpeakerButton=true&micOff=${micOffInitial}&disableDragging=true&lsloglevel=log`}

            // src={`http://localhost:8081/avatar/?avatarId=6875283470632373204&connect=true&hideChatInput=true&hideFS=true&hideChatHistory=true&connect=true&hideMicButton=false&hideHangUpButton=true&speakerOffByDefault=false&hideSpeakerButton=false&micOff=true`}
            // src={`https://digitalhuman.uk/avatar/${avatarConfig.avatarId}?connect=true&hideChatInput=true&hideFS=true&hideChatHistory=true&connect=true&hideMicButton=false&hideHangUpButton=true&speakerOffByDefault=false&hideSpeakerButton=true&micOff=${micOffInitial}`}

            style={{ width: "100%", height: "100%" }}
          ></iframe>
        )}
      </div>
    </>
  )
}

export const TrulienceAvatar = memo(_TrulienceAvatar)
